/* ─────────────────────────────────────────────────────────────────────────
 * Cabinet data: the palette the pixel art is painted in, the 11×11 section
 * icons, and the résumé the window shows. Edit RESUME to change what the
 * cabinet says; the title, captions and window all read from it.
 * ───────────────────────────────────────────────────────────────────────── */

export type Entry = {
    title: string
    when: string
    where: string
    detail: string
    tags?: string[]
}

export type Contact = {
    label: string
    value: string
    href?: string
}

export type SkillTone = 'teal' | 'tan' | 'sage' | 'peach'

export type Skill = {
    label: string
    tone: SkillTone
}

export type Discipline = {
    label: string
    level: number
}

export type Resume = {
    headline: string
    year: string
    role: string
    name: string
    portraitSrc?: string
    contact: Contact[]
    experience: Entry[]
    projects: Entry[]
    education: Entry[]
    skills: Skill[]
    disciplines: Discipline[]
}

/* Eight colours. The canvases paint only in these (dithering mixes them),
 * and the section's CSS variables are fed from the same values. */
export const PAL = {
    cream: '#f3ebcf',
    creamHi: '#fbf4dc',
    plum: '#574349',
    peach: '#e0896a',
    tan: '#ecca8c',
    sage: '#a9c29b',
    teal: '#357471',
    rust: '#b5503c'
} as const

export type IconName = 'contact' | 'experience' | 'projects' | 'education' | 'skills'

/* 11×11 bitmaps, "k" is an inked cell */
export const ICONS: Record<IconName, string[]> = {
    contact: [
        '...........',
        'kkkkkkkkkkk',
        'kk.......kk',
        'k.k.....k.k',
        'k..k...k..k',
        'k...k.k...k',
        'k....k....k',
        'k.........k',
        'k.........k',
        'kkkkkkkkkkk',
        '...........'
    ],
    experience: [
        '...........',
        '...kkkkk...',
        '...k...k...',
        'kkkkkkkkkkk',
        'k.........k',
        'k....k....k',
        'kkkkkkkkkkk',
        'k.........k',
        'k.........k',
        'kkkkkkkkkkk',
        '...........'
    ],
    projects: [
        '.....k.....',
        '....kkk....',
        '....kkk....',
        'kkkkkkkkkkk',
        '.kkkkkkkkk.',
        '..kkkkkkk..',
        '...kkkkk...',
        '..kkkkkkk..',
        '..kkk.kkk..',
        '.kk.....kk.',
        '.k.......k.'
    ],
    education: [
        '...........',
        '.....k.....',
        '...kkkkk...',
        '.kkkkkkkkk.',
        'kkkkkkkkkkk',
        '.kkkkkkkkkk',
        '..kkkkkkk.k',
        '..k.....k.k',
        '..kkkkkkk.k',
        '.........kk',
        '...........'
    ],
    skills: [
        '...........',
        '......kkk..',
        '.....kkk...',
        '....kkk....',
        '...kkk.....',
        '..kkkkkkk..',
        '.....kkk...',
        '....kkk....',
        '...kkk.....',
        '..kk.......',
        '...........'
    ]
}

export const RESUME: Resume = {
    headline: "Portfolio",
    year: "2026",
    role: "Product Designer",
    name: "Mara Ellison",
    // a square photo here is repainted as pixel art in the palette above;
    // left empty, the window shows the drawn pixel portrait instead
    portraitSrc: "",
    contact: [
        { label: "Email", value: "hello@maraellison.com", href: "mailto:hello@maraellison.com" },
        { label: "Location", value: "Lisbon, Portugal" },
        { label: "Status", value: "Open to freelance from November" }
    ],
    experience: [
        {
            when: "2023 — Now",
            title: "Senior product designer",
            where: "Kiln Games",
            detail: "Leads design for a live puzzle game played by two million people a month. Rebuilt onboarding and the in-game shop, lifting day-seven retention by 14%.",
            tags: ["Design systems", "Live ops", "Prototyping"]
        },
        {
            when: "2020 — 2023",
            title: "Product designer",
            where: "Fieldnote",
            detail: "Took a note-taking app from first sketch to launch. Shipped offline sync, handwriting search and a home-screen widget people actually keep.",
            tags: ["Mobile", "Research", "Figma"]
        },
        {
            when: "2018 — 2020",
            title: "Interface developer",
            where: "Northlight Studio",
            detail: "Built interactive campaigns for museums and festivals, from WebGL landing pages to a ticketing flow that sold out in four minutes.",
            tags: ["WebGL", "TypeScript", "Motion"]
        }
    ],
    projects: [
        {
            when: "2025",
            title: "Tidepool",
            where: "Surf forecast app",
            detail: "A pixel-art surf forecast that turns swell, wind and tide data into one daily score, checked every morning by 40,000 surfers along the Atlantic coast.",
            tags: ["Data viz", "Pixel art", "iOS"]
        },
        {
            when: "2024",
            title: "Loop Kit",
            where: "Open-source UI kit",
            detail: "Forty accessible components with keyboard support built in, now used by more than 300 teams and translated into nine languages.",
            tags: ["React", "Accessibility", "Docs"]
        },
        {
            when: "2022",
            title: "Night Market",
            where: "Browser game",
            detail: "A cozy trading game about running a food stall after dark. Designed, drawn and coded in 48 hours, then won the jam's audience award.",
            tags: ["Game jam", "Canvas", "Sound"]
        }
    ],
    education: [
        {
            when: "2021",
            title: "Inclusive design course",
            where: "Online, 10 weeks",
            detail: "Studied screen readers, color vision and motor access, then audited and fixed three of my own shipped products.",
            tags: ["WCAG", "Audits"]
        },
        {
            when: "2019",
            title: "Creative coding residency",
            where: "Studio Nyx",
            detail: "Six weeks building generative installations for a light festival, from shader sketches to a 12-meter projection wall.",
            tags: ["Shaders", "Installations"]
        },
        {
            when: "2014 — 2018",
            title: "BA Communication Design",
            where: "Atlantic Design Institute",
            detail: "Graduated with distinction. Thesis on how game interfaces teach players without a single line of instructions.",
            tags: ["Typography", "Game design"]
        }
    ],
    skills: [
        { label: "UX", tone: 'teal' },
        { label: "UI", tone: 'tan' },
        { label: "TS", tone: 'sage' },
        { label: "CSS", tone: 'peach' },
        { label: "GL", tone: 'teal' },
        { label: "3D", tone: 'tan' },
        { label: "SVG", tone: 'sage' },
        { label: "iOS", tone: 'peach' }
    ],
    disciplines: [
        { label: "Interaction design", level: 5 },
        { label: "Prototyping", level: 5 },
        { label: "Visual design", level: 4 },
        { label: "Front-end code", level: 4 },
        { label: "Motion", level: 3 },
        { label: "Illustration", level: 3 }
    ]
}
